import { InputDate, InputNumber, RadioGroup } from '@teo/components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type CompetenceValidityDuration = 'forever' | 'days' | 'date';

type CompetenceValidityFieldsProps = {
  duration: CompetenceValidityDuration;
  expiryDate?: Date;
  onDurationChange: (duration: CompetenceValidityDuration) => void;
  onExpiryDateChange: (date?: Date) => void;
  onValidForDaysChange: (days: number) => void;
  validForDays: number;
};

export function defaultCompetenceExpiryDate() {
  const date = new Date();
  date.setDate(date.getDate() + 90);
  return date;
}

export function serializeCompetenceValidity({
  duration,
  expiryDate,
  validForDays,
}: {
  duration: CompetenceValidityDuration;
  expiryDate?: Date;
  validForDays: number;
}) {
  if (duration === 'days') {
    return {
      expiryDate: null,
      validForDays:
        Number.isFinite(validForDays) && validForDays >= 1
          ? validForDays
          : null,
    };
  }

  if (duration === 'date' && expiryDate) {
    const endOfDay = new Date(expiryDate);
    endOfDay.setHours(23, 59, 59, 999);
    return {
      expiryDate: endOfDay.toISOString(),
      validForDays: null,
    };
  }

  return { expiryDate: null, validForDays: null };
}

export function CompetenceValidityFields({
  duration,
  expiryDate,
  onDurationChange,
  onExpiryDateChange,
  onValidForDaysChange,
  validForDays,
}: CompetenceValidityFieldsProps) {
  const { t, i18n } = useTranslation();
  const calculatedExpiry = useMemo(() => {
    if (duration === 'forever') return null;
    if (duration === 'date') return expiryDate;
    if (!Number.isFinite(validForDays) || validForDays < 1) return undefined;

    const date = new Date();
    date.setDate(date.getDate() + validForDays);
    return date;
  }, [duration, expiryDate, validForDays]);

  return (
    <div className="mb-4 flex w-full flex-col">
      <div className="text-sm text-black">
        <RadioGroup
          value={duration}
          gap="gap-2"
          label={t('pages.follow_up_users.expiration_date')}
          onChange={(value) =>
            onDurationChange(value as CompetenceValidityDuration)
          }
        >
          <div
            id="competence-validity-duration"
            className="flex flex-col gap-2 sm:flex-row"
          >
            {(['forever', 'days', 'date'] as const).map((option) => (
              <div
                key={option}
                className={`flex-1 rounded-lg border border-grey-02 p-3 ${
                  duration === option ? 'border-secondary-04' : ''
                }`}
              >
                <RadioGroup.Option value={option}>
                  <span className="text-xs">
                    {t(
                      `pages.follow_up_users.manual_assign_deny_duration_${option}`
                    )}
                  </span>
                </RadioGroup.Option>
              </div>
            ))}
          </div>
        </RadioGroup>

        {duration === 'days' && (
          <div className="mt-3 flex items-center gap-2 text-xs text-grey-06">
            <InputNumber
              mode="integer"
              min={1}
              value={validForDays}
              className="max-w-[96px]"
              onChange={(value) => onValidForDaysChange(value ?? 0)}
            />
            <span>
              {t('pages.follow_up_users.manual_assign_deny_days_expiry', {
                date: calculatedExpiry
                  ? new Intl.DateTimeFormat(i18n.language).format(
                      calculatedExpiry
                    )
                  : '—',
              })}
            </span>
          </div>
        )}

        {duration === 'date' && (
          <div className="mt-3 max-w-[220px]">
            <InputDate
              id="competence-validity-date"
              value={expiryDate}
              onChange={onExpiryDateChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}
